<template>
  <div>
    <a-modal
      :visible="previewVisible"
      :footer="null"
      @cancel="previewVisible = false"
    >
      <img alt="preview" style="width: 100%" :src="previewImage" />
    </a-modal>

    <a-modal
      v-model="visible"
      @cancel="handleCancel"
      :maskClosable="false"
      :title="null"
      :closable="false"
      :width="720"
      :footer="false"
      :bodyStyle="{ padding: 0 }"
      wrapClassName="feed-form-modal"
      destroyOnClose
      centered
    >
      <div class="news-form-header">
        <span class="news-form-title">
          <span class="news-form-title-icon icon-club">
            <a-icon type="team" />
          </span>
          {{ isEdit ? $t('activityClub.editPost') : $t('activityClub.addPost') }}
        </span>
        <button
          type="button"
          class="news-form-close"
          :disabled="loading"
          @click="handleCancel"
          aria-label="close"
        >
          <a-icon type="close" />
        </button>
      </div>
      <a-form :form="form" layout="vertical" @submit="handleSubmit">
        <div class="news-form-body">
          <a-form-item :label="$t('actualite.titre')">
            <a-input
              :placeholder="$t('actualite.titre')"
              v-decorator="['title', { rules: [{ required: true, message: $t('requis.titre') }] }]"
            />
          </a-form-item>

          <a-form-item :label="$t('actualite.date')">
            <a-date-picker
              class="full"
              :placeholder="$t('actualite.date')"
              show-time
              format="DD/MM/YYYY HH:mm:ss"
              v-decorator="['plannifDate', { initialValue: moment(), rules: [{ required: true, message: $t('requis.date') }] }]"
            />
          </a-form-item>

          <a-form-item :label="$t('actualite.specificFor')">
            <a-select
              :placeholder="$t('actualite.specificFor')"
              v-decorator="['specificFor', { initialValue: 'all' }]"
              @change="selectedStudents = []"
            >
              <a-select-option value="all">{{ $t("actualite.tous") }}</a-select-option>
              <a-select-option value="students">{{ $t("actualite.students") }}</a-select-option>
              <a-select-option value="levels">{{ $t("actualite.levels") }}</a-select-option>
              <a-select-option value="classrooms">{{ $t("actualite.classrooms") }}</a-select-option>
              <a-select-option value="activities">{{ $t("actualite.activities") }}</a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item
            v-if="form.getFieldValue('specificFor') === 'levels'"
            :label="$t('actualite.levels')"
          >
            <LevelsClassroomsFilter
              v-decorator="['levels', { initialValue: [], rules: [{ required: true, message: $t('enseignant.niveauxRequis') }] }]"
              type="levels"
              :level-options="levelOptions"
              :classes="classes"
            />
          </a-form-item>

          <a-form-item
            v-if="form.getFieldValue('specificFor') === 'classrooms'"
            :label="$t('actualite.classrooms')"
          >
            <LevelsClassroomsFilter
              v-decorator="['classrooms', { initialValue: [], rules: [{ required: true, message: $t('choisir.classe') }] }]"
              type="classrooms"
              :level-options="levelOptions"
              :classes="classes"
            />
          </a-form-item>

          <a-form-item
            v-if="form.getFieldValue('specificFor') === 'students'"
            :label="$t('actualite.students')"
          >
            <multiselect
              v-model="selectedStudents"
              :options="studentList"
              :custom-label="customLabel"
              :multiple="true"
              track-by="_id"
              label="_id"
              :placeholder="$t('sms.selectionnerEleves')"
            />
          </a-form-item>

          <a-form-item
            v-if="form.getFieldValue('specificFor') === 'activities'"
            :label="$t('actualite.activities')"
          >
            <a-select
              mode="multiple"
              :placeholder="$t('actualite.activities')"
              v-decorator="['activities', { initialValue: [], rules: [{ required: true, message: $t('actualite.activitiesRequired') }] }]"
            >
              <a-select-option v-for="a in activitiesList" :key="a._id" :value="a._id">
                {{ a.designation }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item :label="$t('activityClub.activity')">
            <a-select
              :placeholder="$t('activityClub.activity')"
              v-decorator="['activity', { initialValue: OTHER_VALUE }]"
              @change="onActivityChange"
            >
              <a-select-option v-for="a in activitiesList" :key="a._id" :value="a._id">
                {{ a.designation }}
              </a-select-option>
              <a-select-option :value="OTHER_VALUE">{{ $t("activityClub.other") }}</a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item
            v-if="form.getFieldValue('activity') === OTHER_VALUE"
            :label="$t('activityClub.customActivityName')"
          >
            <a-input
              :placeholder="$t('activityClub.customActivityName')"
              v-decorator="['activityCustomName', { rules: [{ required: true, message: $t('requis.titre') }] }]"
            />
          </a-form-item>

          <a-form-item :label="$t('activityClub.price')">
            <a-input-number
              :min="0"
              style="width: 100%"
              v-decorator="['price', { initialValue: 0 }]"
            />
          </a-form-item>

          <a-form-item :label="$t('activityClub.registrationStartDate')">
            <a-date-picker
              class="full"
              :placeholder="$t('activityClub.registrationStartDate')"
              format="DD/MM/YYYY"
              v-decorator="['registrationStartDate', { rules: [{ required: true, message: $t('requis.date') }] }]"
            />
          </a-form-item>

          <a-form-item :label="$t('activityClub.clubStartDate')">
            <a-date-picker
              class="full"
              :placeholder="$t('activityClub.clubStartDate')"
              format="DD/MM/YYYY"
              v-decorator="['clubStartDate', { rules: [{ required: true, message: $t('requis.date') }] }]"
            />
          </a-form-item>

          <a-form-item :label="$t('activityClub.clubEndDate')">
            <a-date-picker
              class="full"
              :placeholder="$t('activityClub.clubEndDate')"
              format="DD/MM/YYYY"
              v-decorator="['clubEndDate', { rules: [{ required: true, message: $t('requis.date') }] }]"
            />
          </a-form-item>

          <a-form-item :label="$t('activityClub.maxPlaces')">
            <a-input-number
              :min="1"
              style="width: 100%"
              v-decorator="['maxPlaces']"
              :placeholder="$t('activityClub.maxPlacesOptional')"
            />
          </a-form-item>

          <a-form-item :label="$t('actualite.description')">
            <quill-editor v-model="description" :options="editorOptions" />
          </a-form-item>

          <a-form-item :label="$t('activityClub.coverImage')">
            <div v-if="coverFileList.length > 0" class="cover-banner">
              <img :src="coverFileList[0].url" alt="" @click="handlePreview(coverFileList[0])" />
              <div class="cover-banner-mask">
                <button type="button" class="cover-banner-btn" @click="handlePreview(coverFileList[0])">
                  <a-icon type="eye" />
                </button>
                <button type="button" class="cover-banner-btn" @click="handleCoverRemove(coverFileList[0])">
                  <a-icon type="delete" />
                </button>
              </div>
            </div>
            <a-upload-dragger
              v-else
              class="cover-banner-dragger"
              accept=".jpg,.jpeg,.png,image/jpeg,image/png"
              :show-upload-list="false"
              :before-upload="beforeCoverUpload"
              :custom-request="() => true"
            >
              <p class="ant-upload-drag-icon"><a-icon type="picture" /></p>
              <p class="ant-upload-text">{{ $t("activityClub.coverImage") }}</p>
              <p class="ant-upload-hint">{{ $t("actualite.cliquer") }}</p>
            </a-upload-dragger>
          </a-form-item>

          <a-form-item :label="$t('activityClub.attachments')">
            <a-upload-dragger
              :file-list="attachmentFiles"
              :before-upload="beforeAttachmentUpload"
              :remove="handleAttachmentRemove"
              multiple
              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              :custom-request="() => true"
            >
              <p class="ant-upload-drag-icon"><a-icon type="inbox" /></p>
              <p class="ant-upload-text">{{ $t("actualite.cliquer") }}</p>
            </a-upload-dragger>
            <ul v-if="existingAttachments.length" class="existing-attachments-list">
              <li v-for="(att, i) in existingAttachments" :key="'existing-' + i">
                <a :href="settings.resourcesUrl.replace('FILE_PATH', att)" target="_blank" rel="noopener noreferrer">
                  <a-icon type="paper-clip" />
                  {{ attachmentName(att) }}
                </a>
                <a-icon type="close" class="remove-attachment-icon" @click="removeExistingAttachment(i)" />
              </li>
            </ul>
          </a-form-item>

          <a-progress
            :stroke-color="{
              from: '#108ee9',
              to: '#87d068',
            }"
            v-if="progress.value > 0"
            :percent="progress.value"
            status="active"
          />
        </div>
        <div v-if="enableClubComments" class="comments-toggle-section">
          <div class="comments-toggle-row">
            <span class="toggle-icon"><a-icon type="message" /></span>
            <div class="toggle-copy">
              <span class="toggle-label">{{ $t("activityClub.enableCommentsForThisPost") }}</span>
              <div class="toggle-hint">
                {{ $t("activityClub.enableCommentsForThisPostDesc") }}
              </div>
            </div>
            <a-switch v-model="enableComments" />
          </div>
        </div>
        <div class="form-actions">
          <button
            type="button"
            class="form-btn ghost"
            :disabled="loading"
            @click="handleCancel"
          >
            {{ $t("action.annuler") }}
          </button>
          <button
            type="submit"
            class="form-btn primary"
            :disabled="loading"
          >
            <a-icon v-if="loading" type="loading" />
            {{ $t("action.enregistrer") }}
          </button>
        </div>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import "quill/dist/quill.snow.css";
import { quillEditor } from "vue-quill-editor";
import { uploadFilesToS3 } from "@/services/uploadService";
import { showApiError } from "@/utilities/showApiError";
import { getAllLevels } from "@/config/LevelConstants";
import LevelsClassroomsFilter from "../components/LevelsClassroomsFilter.vue";
import apiClient from "@/services/axios";

const OTHER_VALUE = "__other__";

export default {
  name: "ActivityClubForm",
  components: { LevelsClassroomsFilter, quillEditor },
  props: {
    studentList: { type: Array, default: () => [] },
    classes: { type: Array, default: () => [] },
    activitiesList: { type: Array, default: () => [] },
    enableClubComments: { type: Boolean, default: true },
  },
  data() {
    return {
      previewVisible: false,
      previewImage: "",
      progress: { value: 0 },
      visible: false,
      loading: false,
      isEdit: false,
      editId: null,
      description: "",
      selectedStudents: [],
      coverFileList: [],
      existingCover: "",
      attachmentFiles: [],
      existingAttachments: [],
      enableComments: true,
      OTHER_VALUE,
      editorOptions: {
        modules: {
          toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
            ["clean"],
          ],
        },
      },
    };
  },
  computed: {
    ...mapState(["settings", "user"]),
    levelOptions() {
      const countryCode =
        this.user?.building?.find((b) => b.dbName === this.settings?.activeBuilding)
          ?.countryCode || "TN";
      const levels = getAllLevels(this.$t.bind(this), countryCode);
      return Object.entries(levels).map(([value, label]) => ({ value, label }));
    },
  },
  created() {
    this.form = this.$form.createForm(this);
  },
  methods: {
    moment,
    handleCoverRemove(file) {
      this.coverFileList = this.coverFileList.filter((f) => f.uid !== file.uid);
      this.existingCover = "";
    },
  attachmentName(att) {
    if (!att) return "";
    const parts = att.split("/");
    return parts[parts.length - 1];
  },
  removeExistingAttachment(index) {
    this.existingAttachments.splice(index, 1);
  },
  handleAttachmentRemove(file) {
    this.attachmentFiles = this.attachmentFiles.filter((f) => f.uid !== file.uid);
    return true;
  },
  getFileExtension(filename) {
    const parts = (filename || "").toLowerCase().split(".");
    return parts.length > 1 ? parts.pop() || "" : "";
  },
  isAllowedClubImage(file) {
    const ext = this.getFileExtension(file.name);
    const mime = (file.type || "").toLowerCase();
    const allowedImageExts = ["jpg", "jpeg", "png"];
    const allowedImageMimes = ["image/jpeg", "image/png"];
    // Reject .jfif even when browsers report MIME as image/jpeg
    return (
      allowedImageExts.includes(ext) &&
      (!mime || allowedImageMimes.includes(mime))
    );
  },
  isAllowedClubAttachment(file) {
    const ext = this.getFileExtension(file.name);
    const mime = (file.type || "").toLowerCase();
    const allowedExts = ["jpg", "jpeg", "png", "pdf", "doc", "docx"];
    const allowedMimes = [
      "image/jpeg",
      "image/png",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowedExts.includes(ext)) return false;
    if (mime && !allowedMimes.includes(mime)) return false;
    if (["jpg", "jpeg", "png"].includes(ext)) {
      return this.isAllowedClubImage(file);
    }
    return true;
  },
  beforeAttachmentUpload(file) {
    if (!this.isAllowedClubAttachment(file)) {
      this.$message.error(this.$t("error.fichiersPDFWord"));
      return false;
    }
    // wrap with uid/status/originFileObj so a-upload-dragger can render + remove it properly
    this.attachmentFiles.push({
      uid: `${Date.now()}-${file.name}`,
      name: file.name,
      status: "done",
      originFileObj: file,
    });
    return false;
  },
    handlePreview(file) {
      this.previewImage = file.url || file.thumbUrl;
      this.previewVisible = true;
    },
    customLabel({ firstName, lastName }) {
      return `${lastName} ${firstName}`;
    },
    inferSpecificFor(item) {
      if (item?.students?.length) return "students";
      if (item?.levels?.length) return "levels";
      if (item?.classrooms?.length) return "classrooms";
      if (item?.activities?.length) return "activities";
      return "all";
    },
    show(item) {
      this.visible = true;
      this.progress = { value: 0 };
      this.isEdit = !!item;
      this.editId = item ? item._id : null;
      this.description = item ? item.desc || "" : "";
      this.selectedStudents = [];
      this.coverFileList = [];
      this.existingCover = item?.coverImage || "";
      this.attachmentFiles = [];
      this.existingAttachments = item?.attachments ? [...item.attachments] : [];
      this.enableComments = item ? item.enableComments !== false : this.enableClubComments;

      if (item?.coverImage) {
        const url = this.settings.resourcesUrl.replace("FILE_PATH", item.coverImage);
        this.coverFileList = [{
          uid: "-1",
          name: "cover",
          status: "done",
          url,
          thumbUrl: url,
          isExisting: true,
          response: item.coverImage,
        }];
      }

      this.$nextTick(() => {
        const activityVal = item?.activity
          ? item.activity._id || item.activity
          : OTHER_VALUE;
        const specificFor = this.inferSpecificFor(item);

        if (specificFor === "students" && item?.students?.length) {
          this.selectedStudents = this.studentList.filter((s) =>
            item.students.some((id) => String(id) === String(s._id))
          );
        }

        this.form.setFieldsValue({
          title: item?.title,
          plannifDate: item?.plannifDate ? moment(item.plannifDate) : moment(),
          specificFor,
          activity: activityVal,
        });

        this.$nextTick(() => {
          this.form.setFieldsValue({
            levels: item?.levels || [],
            classrooms: item?.classrooms || [],
            activities: item?.activities || [],
            activityCustomName: item?.activityCustomName || "",
            price: item?.price || 0,
            registrationStartDate: item?.registrationStartDate
              ? moment(item.registrationStartDate)
              : null,
            clubStartDate: item?.clubStartDate
              ? moment(item.clubStartDate)
              : null,
            clubEndDate: item?.clubEndDate
              ? moment(item.clubEndDate)
              : null,
            maxPlaces: item?.maxPlaces,
          });
        });
      });
    },
    onActivityChange(activityId) {
      if (activityId === OTHER_VALUE) return;
      const act = this.activitiesList.find((a) => a._id === activityId);
      if (act && act.inscription != null) {
        this.form.setFieldsValue({ price: act.inscription });
      }
      if (act && act.start) {
        this.form.setFieldsValue({
          clubStartDate: moment(act.start),
          clubEndDate: act.end ? moment(act.end) : null,
        });
      }
    },
    beforeCoverUpload(file) {
  const ok = this.isAllowedClubImage(file);
  if (!ok) {
    this.$message.error(this.$t("error.fichiers"));
    return false;
  }
  const reader = new FileReader();
  reader.onload = (e) => {
    this.coverFileList = [{
      uid: "-1",
      name: file.name,
      status: "done",
      url: e.target.result,
      thumbUrl: e.target.result,
      originFileObj: file,
    }];
  };
  reader.readAsDataURL(file);
  return false;
},
    handleCancel() {
      this.visible = false;
      this.form.resetFields();
      this.enableComments = this.enableClubComments;
      this.description = "";
      this.progress = { value: 0 };
      this.loading = false;
    },
    buildPayload(values) {
      const payload = {
        title: values.title,
        desc: this.description,
        target: ["parents"],
        plannifDate: values.plannifDate,
        activity: values.activity === OTHER_VALUE ? null : values.activity,
        activityCustomName: values.activity === OTHER_VALUE ? values.activityCustomName : "",
        price: values.price || 0,
        registrationStartDate: values.registrationStartDate,
        clubStartDate: values.clubStartDate,
        clubEndDate: values.clubEndDate,
        maxPlaces: values.maxPlaces || null,
        students: null,
        levels: null,
        classrooms: null,
        activities: null,
        attachments: [...this.existingAttachments],
        coverImage: this.existingCover,
        enableComments: this.enableClubComments ? this.enableComments : false,
      };

      if (values.specificFor === "students") {
        payload.students = this.selectedStudents.map((s) => s._id);
      } else if (values.specificFor === "levels") {
        payload.levels = values.levels;
      } else if (values.specificFor === "classrooms") {
        payload.classrooms = values.classrooms;
      } else if (values.specificFor === "activities") {
        payload.activities = values.activities;
      }
      return payload;
    },
    async handleSubmit(e) {
      e.preventDefault();
      this.form.validateFields(async (err, values) => {
        if (err) return;
        this.loading = true;
        this.progress = { value: 0 };
        try {
          const payload = this.buildPayload(values);

          if (this.coverFileList.length > 0 && !this.coverFileList[0].isExisting) {
            const keys = await uploadFilesToS3(
              [this.coverFileList[0].originFileObj],
              "manager/activity-club",
              this.progress
            );
            payload.coverImage = keys[0];
          } else if (this.coverFileList[0]?.isExisting) {
            payload.coverImage = this.coverFileList[0].response;
          }

          const newFiles = this.attachmentFiles.filter((f) => !f.isExisting);
          if (newFiles.length > 0) {
            const filesToUpload = newFiles.map((f) => f.originFileObj || f);
            const keys = await uploadFilesToS3(
              filesToUpload,
              "manager/activity-club",
              this.progress
            );
            payload.attachments = [...payload.attachments, ...keys];
          }

          if (this.isEdit) {
            await apiClient.put("/activity-club/" + this.editId + "/update", payload);
            this.$message.success(this.$t("success.pubModif"));
          } else {
            await apiClient.put("/activity-club/", payload);
            this.$message.success(this.$t("success.pubAjout"));
          }
          this.$emit("saved");
          this.handleCancel();
        } catch (error) {
          showApiError(this, error);
        } finally {
          this.loading = false;
          this.progress = { value: 0 };
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import "../components/form-shared.scss";

.feed-form-modal {
  .cover-banner {
    position: relative;
    width: 100%;
    height: 168px;
    border: 1px solid #e4e6eb;
    border-radius: 6px;
    overflow: hidden;
    background: #f0f2f5;
  }

  .cover-banner img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    cursor: pointer;
  }

  .cover-banner-mask {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    background: rgba(0, 0, 0, 0.45);
    opacity: 0;
    transition: opacity 0.2s;
  }

  .cover-banner:hover .cover-banner-mask {
    opacity: 1;
  }

  .cover-banner-btn {
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.92);
    color: #1c1e21;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 16px;
  }

  .cover-banner-dragger.ant-upload.ant-upload-drag {
    height: 168px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .existing-attachments-list {
    margin: 10px 0 0;
    padding: 0;
    list-style: none;
  }

  .existing-attachments-list li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 0;
    font-size: 13px;
  }

  .existing-attachments-list a {
    color: #1c1e21;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .remove-attachment-icon {
    cursor: pointer;
    color: #65676b;

    &:hover {
      color: #ff4d4f;
    }
  }
}
</style>
